import { useState, useRef, useEffect } from 'react';
import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
	DropdownMenu,
	DropdownPositioningContainer,
	IconButton,
	IconThreeDots,
} from '@montel/montelpro-shared-components-v2';
import { ActionMenuItem as ActionMenuItemConfig } from '../Table.interfaces';
import ActionMenuItem from './ActionMenuItem';

const StyledDropdownPositioningContainer = styled(DropdownPositioningContainer)`
	display: flex;
	justify-content: center;
`;

const ActionMenuPortalContainer = styled.div<{
	top: number;
	left: number;
	right: number;
}>`
	position: fixed;
	top: ${({ top }) => top}px;
	left: ${({ left }) => left}px;
	right: ${({ right }) => right}px;
	z-index: 9999;
`;

interface ActionMenuProps<T> {
	row: T;
	items: ActionMenuItemConfig<T>[];
	alignRight?: boolean;
	unbound?: boolean;
}

function ActionMenu<T>({
	row,
	items,
	alignRight = true,
	unbound = false,
}: ActionMenuProps<T>) {
	const [isOpen, setIsOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState({
		top: 0,
		left: 0,
		right: 0,
	});
	const buttonRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setMenuPosition({
				top: rect.bottom - 30,
				left: rect.left,
				right: window.innerWidth - rect.right - 10,
			});
		}
	}, [isOpen]);

	return (
		<StyledDropdownPositioningContainer>
			<div ref={buttonRef}>
				<IconButton
					icon={<IconThreeDots boxSize="24" />}
					variant="tertiary"
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						setIsOpen((prev) => !prev);
					}}
					aria-label="Row actions"
					aria-haspopup="true"
					aria-expanded={isOpen}
				/>
			</div>
			{isOpen &&
				(() => {
					const menu = (
						<DropdownMenu
							alignToRight={alignRight}
							closeMenu={() => setIsOpen(false)}
						>
							{items.map((item) => {
								const disabled =
									item.isDisabled?.(row) ?? false;
								return (
									<ActionMenuItem
										key={item.name}
										onClick={(e) => {
											e.stopPropagation();
											if (disabled) return;
											setIsOpen(false);
											item.onClick(row);
										}}
										isDisabled={disabled}
									>
										{item.name}
									</ActionMenuItem>
								);
							})}
						</DropdownMenu>
					);

					return unbound
						? ReactDOM.createPortal(
								<ActionMenuPortalContainer
									top={menuPosition.top}
									left={menuPosition.left}
									right={menuPosition.right}
								>
									{menu}
								</ActionMenuPortalContainer>,
								document.body,
						  )
						: menu;
				})()}
		</StyledDropdownPositioningContainer>
	);
}

export default ActionMenu;
